import { Disclosure, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import arrowDown from "assets/images/arrow-down.svg";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-hot-toast";
import { INTEGRATE_LINE } from "services/users";

const LINE_ICON_S3_URL =
  "https://auth-service-public.s3.ap-northeast-1.amazonaws.com/assets/line_logo.png";

const getSafeLocalStorageUserInfo = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }

  const stored = window.localStorage.getItem("userInfo");
  if (!stored) {
    return {};
  }

  try {
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const UserInfo = (props) => {
  const memberInfo = useSelector((state) => state.member.memberInfo);
  const [isCouponExpired, setIsCouponExpired] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [lineIconSrc, setLineIconSrc] = useState(LINE_ICON_S3_URL);
  const { t, i18n } = useTranslation();

  // Get user info from localStorage to check LINE linking status.
  const userInfo = getSafeLocalStorageUserInfo();
  const lineIdentities = Array.isArray(userInfo?.identities)
    ? userInfo.identities
    : [];
  const isLineLinked = lineIdentities.some(
    (identity) => identity?.provider === "line"
  );

  const handleLineIntegration = async () => {
    try {
      setLineLoading(true);
      const result = await INTEGRATE_LINE();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        console.error("LINE integration error: Missing redirect URL in response.", result);
        toast.error(t("line_error_default"));
      }
    } catch (error) {
      console.error("LINE integration error:", error);
      toast.error(t("line_error_default"));
    } finally {
      setLineLoading(false);
    }
  };

  const renderLineIcon = () => {
    return (
      <img
        alt="line_icon"
        loading="lazy"
        src={lineIconSrc}
        className="w-8 h-8 object-contain flex-shrink-0"
        onError={() => {
          if (lineIconSrc !== "/line-icon.png") {
            setLineIconSrc("/line-icon.png");
          }
        }}
      />
    );
  };

  useEffect(() => {
    const now = moment();
    // Checking coupon's user is expired or not, to display the expiry text message
    if (memberInfo?.expire_date) {
      if (now.isAfter(moment(memberInfo?.expire_date))) {
        setIsCouponExpired(true);
      } else {
        setIsCouponExpired(false);
      }
    }
  }, [memberInfo]);

  return (
    <>
      {isCouponExpired && (
        <div className="text-sm text-center mb-4 text-red-500 font-semibold">
          {t("coupon_expired")}
        </div>
      )}
      <div className="mt-2 text-sm text-neutral-700 text-center">
        {t("expired_date")}：{memberInfo?.expire_date ?? "-"}
      </div>
      <div className="text-2xl text-neutral-700 text-center">
        {memberInfo?.first_name} {memberInfo?.middle_name ? memberInfo?.middle_name+' '+memberInfo?.last_name : memberInfo?.last_name }
      </div>
      
      {/* LINE Integration Section */}
      <div className="mt-6 px-5">
        {isLineLinked ? (
          <div className="flex items-center justify-center gap-3 h-[44px] self-center text-sm text-center text-white whitespace-nowrap w-full bg-gray-400 rounded-lg">
            {renderLineIcon()}
            {t("line_linked")}
          </div>
        ) : (
          <button
            onClick={handleLineIntegration}
            disabled={lineLoading}
            className="flex items-center justify-center gap-3 h-[44px] self-center text-sm text-center text-white whitespace-nowrap w-full bg-[#06C755] rounded-lg hover:scale-105 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {renderLineIcon()}
            {lineLoading ? t("line_linking_loading") : t("line_link_button")}
          </button>
        )}
      </div>
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex w-full justify-center gap-2.5 text-sm font-normal px-5 mt-6">
              {t("about_coupon")}
              <img
                alt="coupon"
                loading="lazy"
                src={arrowDown}
                className="my-auto w-3.5 aspect-[1.41] fill-neutral-700"
              />
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel>
                <div className="mt-2 text-sm font-light leading-5 px-5 py-2.5">
                  {t("coupon_usage_period")}
                  <br />
                  {t("items_departments")}
                  <br />
                  {t("items_description")}
                  <br />
                  {t("present_coupon")}
                  <br />
                  {t("coupon_cannot_used")}
                  <br />
                  {t("coupon_description")}
                  {i18n.language === "kr" && <br />}
                  {t("coupon_renewal")} 
                  <br />
                  <br />
                  <span className="font-semibold">
                    Stores where the discount coupon can be used
                  </span>
                  <br />
                  Hankyu Umeda Main Store
                  <br />
                  Hanshin Umeda Main Store
                  <br />
                  Hankyu Men's Osaka
                  <br />
                  Hankyu Men's Tokyo
                  <br />
                  Hakata Hankyu
                  <br />
                  Kobe Hankyu
                </div>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
      <div
        onClick={() =>
          window.open(
            "https://website.hankyu-dept.co.jp/fl/taxrefund/index.html",
            "_blank"
          )
        }
        className="cursor-pointer mt-2 text-sm font-light text-center text-black underline whitespace-nowrap"
      >
        {t("tax_exemption")}
      </div>
      <div
        onClick={() =>
          window.open(
            "https://www.h2o-retailing.co.jp/en/privacy.html",
            "_blank"
          )
        }
        className="cursor-pointer underline text-black font-light text-center mt-2 text-sm"
      >
        {t("privacy_policy")}
      </div>
    </>
  );
};

export default UserInfo;
